import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { clsx } from 'clsx'
import {
  Bell,
  ChevronLeft,
  ChevronRight,
  Megaphone,
  Search,
} from 'lucide-react'
import { toast } from 'sonner'
import {
  announcementApi,
  type Announcement,
  type AnnouncementCategory,
  type AnnouncementScope,
} from '@/api/announcements'
import { AnnouncementDetailModal } from '@/components/admin/AnnouncementDetailModal'

const formatDateDot = (dateString: string) => {
  const [datePart] = dateString.split('T')
  return datePart.replace(/-/g, '.')
}

export default function AdminAnnouncements() {
  const { t } = useTranslation(['admin', 'common'])
  const [activeTab, setActiveTab] = useState<'all' | 'center'>('all')
  const [category, setCategory] = useState<AnnouncementCategory>('all')
  const [page, setPage] = useState(0)
  const [keyword, setKeyword] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null)

  const pageSize = 10
  const scope: AnnouncementScope = activeTab === 'all' ? 'global' : 'center'

  const listQuery = useQuery({
    queryKey: ['announcements', scope, category, page, pageSize, keyword],
    queryFn: () =>
      announcementApi.getList({
        page,
        size: pageSize,
        keyword: keyword || undefined,
        scope,
        category,
      }),
  })

  const announcements = listQuery.data?.announcements || []
  const totalPages = listQuery.data?.totalPages || 0

  const handleSearch = () => {
    setKeyword(searchInput)
    setPage(0)
  }

  const handleTabChange = (tab: 'all' | 'center') => {
    setActiveTab(tab)
    setPage(0)
    setKeyword('')
    setSearchInput('')
  }

  const handleCategoryChange = (next: AnnouncementCategory) => {
    setCategory(next)
    setPage(0)
  }

  const handleViewDetail = async (announcement: Announcement) => {
    try {
      const detail = await announcementApi.getById(announcement.announcementId)
      setSelectedAnnouncement(detail)
    } catch {
      toast.error(t('admin:announcement.toast.loadFailed'))
    }
  }

  const getKindBadge = (announcement: Announcement) => (
    <span
      className={clsx(
        'inline-flex min-w-12 justify-center rounded-full px-2 py-0.5 text-xs font-medium',
        announcement.isPinned ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600',
      )}
    >
      {announcement.isPinned
        ? t('admin:announcement.badge.important')
        : t('admin:announcement.badge.general')}
    </span>
  )

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('admin:announcement.title')}</h1>
      </div>

      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-lg w-fit">
        <button
          type="button"
          onClick={() => handleTabChange('all')}
          className={clsx(
            'flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors',
            activeTab === 'all'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700',
          )}
        >
          <Bell className="w-4 h-4" /> {t('admin:announcement.tabs.all')}
        </button>
        <button
          type="button"
          onClick={() => handleTabChange('center')}
          className={clsx(
            'flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors',
            activeTab === 'center'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-500 hover:text-gray-700',
          )}
        >
          <Megaphone className="w-4 h-4" /> {t('admin:announcement.tabs.center')}
        </button>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
          {(['all', 'pinned', 'normal'] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => handleCategoryChange(value)}
              className={clsx(
                'px-3 py-2 text-sm font-medium rounded-md transition-colors',
                category === value
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700',
              )}
            >
              {t(`admin:announcement.filter.${value}`)}
            </button>
          ))}
        </div>

        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder={t('admin:announcement.search.placeholder')}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <button
          type="button"
          onClick={handleSearch}
          className="px-4 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700"
        >
          {t('common:button.search')}
        </button>
      </div>

      {listQuery.isLoading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-b-2 border-primary-600 rounded-full animate-spin" />
        </div>
      ) : announcements.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          {t('admin:announcement.empty')}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="w-full table-fixed text-sm">
            <colgroup>
              <col className="w-24" />
              <col />
              <col className="w-36" />
              <col className="w-24" />
            </colgroup>
            <thead className="bg-gray-50 text-xs font-medium uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3 text-left">{t('admin:announcement.table.kind')}</th>
                <th className="px-4 py-3 text-left">{t('admin:announcement.table.title')}</th>
                <th className="px-4 py-3 text-left">{t('admin:announcement.table.createdAt')}</th>
                <th className="px-4 py-3 text-right">{t('admin:announcement.table.viewCount')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {announcements.map((ann) => (
                <tr
                  key={ann.announcementId}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => handleViewDetail(ann)}
                >
                  <td className="px-4 py-3">{getKindBadge(ann)}</td>
                  <td className="px-4 py-3">
                    <span className="block truncate font-medium text-gray-900">{ann.title}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{formatDateDot(ann.createdAt)}</td>
                  <td className="px-4 py-3 text-right text-gray-500">{ann.viewCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-4 mt-6">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="p-2 rounded-lg border hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-gray-600">
            {page + 1} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            className="p-2 rounded-lg border hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {selectedAnnouncement && (
        <AnnouncementDetailModal
          announcement={selectedAnnouncement}
          onClose={() => setSelectedAnnouncement(null)}
        />
      )}
    </div>
  )
}
